import { SocketClient } from "cbcore-ts"
import * as pako from "pako"
import { IS_NOT, NO, UIBaseButton, UIButton, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { CBDialogViewShower } from "../CB/CBDialogViewShower"
import { RHButton } from "./RHButton"
import { RHColor } from "./RHColor"
import { formatRHDateTime } from "./Utils_slop/RHDateFormatting"
import { RHButtons, RHForms } from "./Utils_slop/RHDesignTokens"
import { messageOfError } from "../../Core/RHErrorMessage"
import type { Types } from "mongoose"
import type { MongooseReference } from "../../../../mongooseUtil"
import type { RHUserProfilePublic } from "../../../../models/UserModels"
import { populatedValueOfReference, referenceId } from "../../Core/RHReference"

export type RHFileItemViewVariant = "full" | "compactDownload"

/** A file as the item shows it: a populated procurement file, the metadata the server returns for file ids, or a fresh upload. */
export interface RHFileItemData {
    _id?: Types.ObjectId | string
    id?: string
    name: string
    createdAt?: Date | string
    uploadedAt?: Date | string
    uploadedBy?: MongooseReference<RHUserProfilePublic>
    uploaderName?: string
    downloadCount?: number
}


export class RHFileItemView extends UIView {

    static readonly animationDuration = 0.2 // seconds

    static variant = {
        full: "full" as const,
        compactDownload: "compactDownload" as const
    }

    static ICON_FILE(): string {
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${RHForms.fileItemFileIconSvgSize}" height="${RHForms.fileItemFileIconSvgSize}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
  <path d="M14 2v6h6"/>
  <path d="M8 13h8"/>
  <path d="M8 17h5"/>
</svg>`
    }

    static ICON_DELETE(): string {
        return RHButtons.deleteIconHTML
    }

    // ── Header row ────────────────────────────────────────────────────────────

    // The filename acts as the expand/collapse button
    readonly nameButton = new UIBaseButton()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor
        })
        .addedAsSubviewToView(this)

    readonly fileIconLabel = new UITextView()
        .configuredWithObject({
            text: RHFileItemView.ICON_FILE(),
            backgroundColor: RHForms.fileItemFileIconBackgroundColor,
            textColor: RHForms.fileItemFileIconTextColor,
            textAlignment: UITextView.textAlignment.center,
            userInteractionEnabled: NO,
            style: {
                borderRadius: RHForms.fileItemFileIconBorderRadius,
                border: `1px solid ${RHForms.fileItemFileIconBorderColor.stringValue}`,
                color: RHForms.fileItemFileIconTextColor.stringValue,
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }
        })
        .addedAsSubviewToView(this)

    readonly nameLabel = new UITextView()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            textColor: RHForms.fileItemNameTextColor,
            isSingleLine: YES,
            userInteractionEnabled: NO,
            style: {
                fontFamily: RHForms.fileItemNameFontFamily,
                fontSize: RHForms.fileItemNameFontSize,
                fontWeight: RHForms.fileItemNameFontWeight,
                textOverflow: "ellipsis",
                pointerEvents: "none"
            }
        })
        .addedAsSubviewToView(this)

    readonly chevronButton = new UIButton()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            contentPadding: 0,
            colors: {
                background: {
                    normal: UIColor.transparentColor,
                    highlighted: UIColor.transparentColor,
                    selected: UIColor.transparentColor,
                    hovered: UIColor.transparentColor
                },
                titleLabel: {
                    normal: RHColor.textMuted,
                    highlighted: RHColor.textMuted,
                    selected: RHColor.textMuted,
                    hovered: RHColor.textMuted
                }
            },
            style: {
                fontSize: "10px",
                userSelect: "none",
                cursor: "pointer",
                border: "none",
                outline: "none",
                boxShadow: "none"
            }
        })
        .addedAsSubviewToView(this)

    readonly downloadButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: RHButton.ICON_DOWNLOAD(1, RHForms.fileItemDownloadIconSvgSize),
                fontSize: RHForms.fileItemDownloadIconSvgSize,
                hoverText: "Laadi alla"
            },
            contentPadding: 0,
            style: {
                borderRadius: RHForms.fileItemActionButtonBorderRadius,
                padding: "0px"
            }
        })
        .addedAsSubviewToView(this)

    readonly deleteButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: RHFileItemView.ICON_DELETE(),
                fontSize: 18,
                hoverText: "Kustuta"
            },
            contentPadding: 0,
            style: {
                borderRadius: RHForms.fileItemActionButtonBorderRadius,
                padding: "0px"
            }
        })
        .addedAsSubviewToView(this)

    // ── Progress (uploading state only) ───────────────────────────────────────

    readonly progressLabel = new UITextView()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            textColor: RHColor.textSecondary,
            fontSize: 12,
            textAlignment: UITextView.textAlignment.right
        })
        .addedAsSubviewToView(this)

    // ── Expandable content ────────────────────────────────────────────────────

    // Clips the sliding metadata row
    readonly contentContainer = new UIView()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            style: { overflow: "hidden" }
        })
        .addedAsSubviewToView(this)

    readonly metadataLabel = new UITextView()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            textColor: RHForms.fileItemMetadataTextColor,
            isSingleLine: YES,
            style: {
                fontSize: RHForms.fileItemMetadataFontSize,
                fontWeight: "normal"
            }
        })
        .addedAsSubviewToView(this.contentContainer)

    // ── State ─────────────────────────────────────────────────────────────────

    _fileId?: string
    _fileName: string = ""
    _isUploading: boolean = false
    _expanded: boolean = NO
    _metadataHeight: number = 0
    _downloadCount: number | undefined = undefined
    _metadataPrefixParts: string[] = []
    _rawMetadataText: string = ""
    _variant: RHFileItemViewVariant = RHFileItemView.variant.full
    animated: boolean = NO
    shouldDeleteFileWhenRemovedFromList = YES

    constructor(elementID?: string, public onDelete?: (fileItemView: RHFileItemView) => void) {

        super(elementID);

        this._applyVariantVisualStyle()

        this.downloadButton.variant = RHForms.fileItemDownloadButtonVariant
        this.deleteButton.variant = RHForms.fileItemDeleteButtonVariant
        this.downloadButton.contentPadding = 0
        this.deleteButton.contentPadding = 0

        this.progressLabel.hidden = true
        this.downloadButton.hidden = true
        this.deleteButton.hidden = true
        this.chevronButton.hidden = true
        this.contentContainer.style.height = "0px"

        // noinspection JSConstantReassignment
        this.nameButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            if (this._hasMetadata()) {
                this.expanded = !this._expanded
            }
        }

        // noinspection JSConstantReassignment
        this.chevronButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.expanded = !this._expanded
        }

        // noinspection JSConstantReassignment
        this.downloadButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            if (this._fileId) {
                await this.downloadFile(this._fileId, this._fileName)
            }
        }

        // noinspection JSConstantReassignment
        this.deleteButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            if (this._fileId) {
                if (this.shouldDeleteFileWhenRemovedFromList) {
                    await this.deleteFile(this._fileId)
                }
                else {
                    this.onDelete?.(this)
                }
            }
        }
    }


    // ── Public API ────────────────────────────────────────────────────────────

    get variant(): RHFileItemViewVariant {
        return this._variant
    }

    set variant(value: RHFileItemViewVariant) {
        if (this._variant === value) {
            return
        }
        this._variant = value
        this.metadataLabel.text = this._displayMetadataText()
        this.chevronButton.hidden = this._isCompactDownload() || IS_NOT(this._hasMetadata())
        this._applyVariantVisualStyle()
        this._updateNameButtonCursor()
        this.setNeedsLayoutUpToRootView()
    }

    get expanded(): boolean {
        return this._expanded
    }

    set expanded(expanded: boolean) {
        if (this._expanded === expanded) { return }
        this._expanded = expanded
        this._updateChevron()
        this._performContentTransition()
        this.setNeedsLayoutUpToRootView()
    }


    configureAsUploading(fileName: string) {
        this._fileName = fileName
        this._isUploading = true
        this._expanded = NO

        this.nameLabel.text = fileName
        this.nameLabel.textColor = RHColor.textSecondary
        this.nameLabel.configureWithObject({
            style: {
                //@ts-ignore
                fontWeight: "500"
            }
        })

        this.progressLabel.text = "Laadin üles… 0%"
        this.progressLabel.hidden = false

        this.downloadButton.hidden = true
        this.deleteButton.hidden = true
        this.chevronButton.hidden = true
        this.contentContainer.style.height = "0px"

        this._updateNameButtonCursor()

        this.setNeedsLayout()
    }

    /**
     * Lightweight configure — used when only the id and name are available
     * (e.g. right after upload confirmation, before any server metadata is known).
     * No metadata row, no chevron.
     */
    configureWithFileIdAndName(
        fileId: string,
        fileName: string,
        downloadEnabled = YES,
        deleteEnabled = YES
    ) {
        this._applyFileState(fileId, fileName, downloadEnabled, deleteEnabled, "")
    }

    /**
     * Rich configure — used when a full server file object is available.
     * Reads createdAt, uploadedBy.displayName and downloadCount from the object
     * and shows them in the expandable metadata row.
     */
    configureWithFile(
        fileData: RHFileItemData,
        downloadEnabled = YES,
        deleteEnabled = YES
    ) {
        const fileId: string = referenceId(fileData._id) ?? fileData.id ?? ""
        const fileName: string = fileData.name

        const parts: string[] = []

        // createdAt (from procurement populate) or uploadedAt (from RetrieveFilesMetadata)
        const dateValue = fileData.createdAt ?? fileData.uploadedAt
        if (dateValue) {
            parts.push(`Lisatud: ${formatRHDateTime(new Date(dateValue))}`)
        }

        // uploadedBy.displayName (from procurement populate) or uploaderName (from RetrieveFilesMetadata)
        const uploaderName: string | undefined =
            populatedValueOfReference(fileData.uploadedBy)?.displayName || fileData.uploaderName || undefined
        if (uploaderName) {
            parts.push(`Üleslaadija: ${uploaderName}`)
        }

        this._downloadCount = fileData.downloadCount
        this._metadataPrefixParts = [...parts]  // snapshot before appending count

        if (this._downloadCount !== undefined) {
            if (this._downloadCount === 1) {
                parts.push("1 allalaadimine")
            }
            else {
                parts.push(`${this._downloadCount} allalaadimist`)
            }
        }

        this._applyFileState(fileId, fileName, downloadEnabled, deleteEnabled, parts.join("  ·  "))
    }


    updateProgress(progress: number) {
        if (this._isUploading) {
            this.progressLabel.text = `Laadin üles… ${Math.round(progress)}%`
        }
    }


    // ── Private helpers ───────────────────────────────────────────────────────

    _isCompactDownload(): boolean {
        return this._variant === RHFileItemView.variant.compactDownload
    }

    _applyVariantVisualStyle() {
        const isCompact = this._isCompactDownload()
        ;(this as RHFileItemView).configureWithObject({
            backgroundColor: isCompact
                             ? RHForms.fileItemCompactBackgroundTintColor
                             : RHForms.fileItemBackgroundTintColor,
            style: {
                borderRadius: isCompact
                              ? RHForms.fileItemCompactBorderRadius
                              : RHForms.fileItemBorderRadius,
                border: `1px solid ${
                    isCompact
                    ? RHForms.fileItemCompactBorderColor.stringValue
                    : RHForms.fileItemBorderColor.stringValue
                }`,
                boxShadow: isCompact
                           ? RHForms.fileItemCompactBoxShadow
                           : RHForms.fileItemBoxShadow,
                overflow: "clip"
            }
        })
        this.fileIconLabel.hidden = isCompact
        this.nameLabel.hidden = NO
        this.contentContainer.hidden = isCompact
        const downloadIconSize = isCompact
                                 ? RHForms.fileItemCompactDownloadIconSvgSize
                                 : RHForms.fileItemDownloadIconSvgSize
        this.downloadButton.titleLabel.text = RHButton.ICON_DOWNLOAD(1, downloadIconSize)
        this.downloadButton.titleLabel.fontSize = downloadIconSize
        this.nameLabel.configureWithObject({
            style: {
                fontFamily: RHForms.fileItemNameFontFamily,
                fontSize: isCompact
                          ? RHForms.fileItemCompactNameFontSize
                          : RHForms.fileItemNameFontSize,
                fontWeight: isCompact
                            ? RHForms.fileItemCompactNameFontWeight
                            : RHForms.fileItemNameFontWeight,
                textOverflow: "ellipsis",
                pointerEvents: "none"
            }
        })
    }

    _displayMetadataText(): string {
        if (this._isCompactDownload()) {
            return ""
        }
        return this._rawMetadataText
    }

    _applyFileState(
        fileId: string,
        fileName: string,
        downloadEnabled: boolean,
        deleteEnabled: boolean,
        metadataText: string
    ) {
        this._fileId = fileId
        this._fileName = fileName
        this._isUploading = false
        this._expanded = NO
        this._rawMetadataText = metadataText

        this.nameLabel.text = fileName
        this.nameLabel.textColor = RHForms.fileItemNameTextColor
        this.nameLabel.configureWithObject({
            style: {
                //@ts-ignore
                fontSize: this._isCompactDownload()
                          ? RHForms.fileItemCompactNameFontSize
                          : RHForms.fileItemNameFontSize,
                fontWeight: this._isCompactDownload()
                            ? RHForms.fileItemCompactNameFontWeight
                            : RHForms.fileItemNameFontWeight
            }
        })

        this.progressLabel.hidden = true

        const displayMetadataText = this._displayMetadataText()
        if (displayMetadataText) {
            this.metadataLabel.text = displayMetadataText
            this.chevronButton.hidden = false
        }
        else {
            this.metadataLabel.text = ""
            this.chevronButton.hidden = true
        }

        this.contentContainer.style.height = "0px"
        this._updateChevron()
        this._updateNameButtonCursor()

        this.downloadButton.hidden = IS_NOT(downloadEnabled)
        this.deleteButton.hidden = IS_NOT(deleteEnabled)

        this.setNeedsLayout()
    }

    _hasMetadata(): boolean {
        return this.metadataLabel.text.length > 0
    }

    _updateChevron() {
        this.chevronButton.titleLabel.text = this._expanded ? "▲" : "▼"
    }

    _updateNameButtonCursor() {
        this.nameButton.style.cursor = this._hasMetadata() ? "pointer" : "default"
    }

    _performContentTransition() {
        const targetHeight = `${this._expanded ? this._metadataHeight : 0}px`
        const targetTranslate = this._expanded ? "translateY(0%)" : "translateY(-100%)"

        if (this._expanded) {
            this.metadataLabel.style.transform = "translateY(-100%)"
            this.contentContainer.style.height = "0px"
        }
        else {
            this.metadataLabel.style.transform = "translateY(0%)"
            this.contentContainer.style.height = `${this._metadataHeight}px`
        }

        if (!this.animated) {
            this.metadataLabel.style.transform = targetTranslate
            this.contentContainer.style.height = targetHeight
            return
        }

        UIView.animateViewOrViewsWithDurationDelayAndFunction(
            [this.metadataLabel, this.contentContainer],
            RHFileItemView.animationDuration,
            0,
            "ease",
            () => {
                this.metadataLabel.style.transform = targetTranslate
                this.contentContainer.style.height = targetHeight
            },
            () => { /* complete */ }
        )
    }

    async downloadFile(fileId: string, fileName: string) {
        try {
            const companyId = await RHCore.sharedInstance.companyAffiliationStore
                .activeCompanyIdAfterLoadingAffiliations()
            const downloadData = (await SocketClient.GetPresignedDownloadUrl({
                fileId,
                companyId
            })).result

            if (!downloadData?.url) {
                throw new Error("Failed to get download URL")
            }

            const response = await fetch(downloadData.url)
            if (!response.ok) {
                throw new Error("Download failed")
            }

            const blob = await response.blob()

            const finalBlob = downloadData.wasCompressed
                              ? await this.decompressBlob(blob)
                              : blob

            const url = window.URL.createObjectURL(finalBlob)
            const a = document.createElement("a")
            a.href = url
            a.download = fileName
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)

            this._incrementDownloadCountDisplay()

        }
        catch (error) {
            console.error("Download error:", error)
            CBDialogViewShower.alert(`Viga faili allalaadimisel: ${messageOfError(error) ?? ""}`)
        }
    }

    _incrementDownloadCountDisplay() {
        if (this._downloadCount === undefined) {
            return
        }
        this._downloadCount++
        const parts = [...this._metadataPrefixParts, `${this._downloadCount} allalaadimist`]
        this._rawMetadataText = parts.join("  ·  ")
        this.metadataLabel.text = this._displayMetadataText()
        this.setNeedsLayout()
    }

    async decompressBlob(blob: Blob): Promise<Blob> {
        const arrayBuffer = await blob.arrayBuffer()
        const uint8Array = new Uint8Array(arrayBuffer)
        const decompressed = pako.ungzip(uint8Array)
        return new Blob([decompressed])
    }

    async deleteFile(fileId: string) {
        try {

            const response = await SocketClient.DeleteFile({ fileId })

            if (response.result?.success) {
                if (this.onDelete) {
                    this.onDelete(this)
                }
                this.removeFromSuperview()
                return
            }

            // Removing a file still tied to a live submitted offer doesn't need
            // to delete the record at all — amending/resubmitting simply won't
            // reference it anymore, and the original stays intact on the now-
            // superseded submission (see isAssociationBlockingDeletion's "Offer"
            // case in FilesController.ts). Detach it from this form locally
            // instead of showing this specific, expected rejection as an error.
            // sendErrorResponse() wraps the plain string sent server-side into
            // { _isCBSocketErrorMessage: true, messageData: <the string> } -
            // errorResult is that whole wrapper, not the string itself.
            const errorMessageData = (response.errorResult as { messageData?: unknown } | undefined)?.messageData
            if (errorMessageData === "File is part of a submitted offer and cannot be deleted") {
                if (this.onDelete) {
                    this.onDelete(this)
                }
                this.removeFromSuperview()
                return
            }

            CBDialogViewShower.alert("Viga faili kustutamisel")

        }
        catch (error) {
            console.error("Delete error:", error)
            CBDialogViewShower.alert("Viga faili kustutamisel")
        }
    }


    get fileId(): string | undefined {
        return this._fileId
    }

    _layoutCompactDownloadSubviews() {
        const bounds = this.contentBoundsWithInset(0)
        const headerFrame = bounds.rectangleWithHeight(RHForms.fileItemCompactMinRowHeight)
        let nextX = 0

        if (!this.downloadButton.hidden) {
            this.downloadButton.frame = headerFrame
                .rectangleWithWidth(RHForms.fileItemCompactActionButtonSize)
                .rectangleWithHeight(RHForms.fileItemCompactActionButtonSize, 0.5)
                .rectangleByAddingX(nextX)
            nextX += RHForms.fileItemCompactActionButtonSize + RHForms.fileItemCompactNameGap
        }

        const deleteButtonReserve = this.deleteButton.hidden
                                    ? 0
                                    : RHForms.fileItemCompactActionButtonSize + RHForms.fileItemCompactActionButtonGap
        const nameWidth = Math.max(0, headerFrame.width - nextX - deleteButtonReserve)
        this.nameLabel.frame = headerFrame
            .rectangleByAddingX(nextX)
            .rectangleWithWidth(nameWidth)
        this.nameButton.frame = this.nameLabel.frame

        if (!this.deleteButton.hidden) {
            this.deleteButton.frame = headerFrame
                .rectangleWithWidth(RHForms.fileItemCompactActionButtonSize)
                .rectangleWithHeight(RHForms.fileItemCompactActionButtonSize, 0.5)
                .rectangleWithX(headerFrame.max.x - RHForms.fileItemCompactActionButtonSize)
        }

        this.fileIconLabel.frame = headerFrame.rectangleWithWidth(0)
        this.chevronButton.frame = headerFrame.rectangleWithWidth(0)
        this.contentContainer.frame = headerFrame.rectangleForNextRow(0, 0)
    }


    // ── Layout ────────────────────────────────────────────────────────────────

    override layoutSubviews() {
        super.layoutSubviews()

        if (this._isCompactDownload() && !this._isUploading) {
            this._layoutCompactDownloadSubviews()
            return
        }

        const padding = this.core.paddingLength
        const labelHeight = Math.max(padding * 2, RHForms.fileItemMinRowHeight - padding)
        const chevronWidth = padding * 1.5
        const iconColumnWidth = RHForms.fileItemFileIconColumnWidth
        const actionButtonSize = RHForms.fileItemActionButtonSize
        const actionGap = RHForms.fileItemActionButtonGap
        const bounds = this.contentBoundsWithInsets(
            padding * 0.5,
            padding * 0.5,
            0,
            padding * 0.5
        )
        const headerFrame = bounds.rectangleWithHeight(labelHeight)
        const iconFrame = headerFrame
            .rectangleWithWidth(iconColumnWidth)
            .rectangleWithWidth(RHForms.fileItemFileIconSize, 0.5)
            .rectangleWithHeight(RHForms.fileItemFileIconSize, 0.5)

        this.fileIconLabel.frame = iconFrame

        if (this._isUploading) {
            const progressWidth = Math.min(headerFrame.width * 0.35, 160)
            const nameWidth = Math.max(0, headerFrame.width - iconColumnWidth - progressWidth - padding * 0.5)

            this.nameLabel.frame = headerFrame
                .rectangleByAddingX(iconColumnWidth)
                .rectangleWithWidth(nameWidth)
            this.progressLabel.frame = headerFrame.rectangleWithWidth(progressWidth, 1)
            this.nameButton.frame = headerFrame.rectangleWithWidth(
                Math.max(0, headerFrame.width - progressWidth - padding * 0.5)
            )
            this.contentContainer.frame = headerFrame.rectangleForNextRow(0, 0)
            return
        }

        const actionViews: UIView[] = []
        const actionWidths: number[] = []
        if (!this.downloadButton.hidden) {
            actionViews.push(this.downloadButton)
            actionWidths.push(actionButtonSize)
        }
        if (!this.deleteButton.hidden) {
            actionViews.push(this.deleteButton)
            actionWidths.push(actionButtonSize)
        }

        const actionsWidth = actionWidths.reduce((sum, w) => sum + w, 0)
            + Math.max(0, actionWidths.length - 1) * actionGap
        const hasChevron = !this.chevronButton.hidden
        const nameAreaWidth = Math.max(
            iconColumnWidth,
            headerFrame.width - actionsWidth - (hasChevron ? chevronWidth + padding * 0.25 : 0)
        )

        this.nameButton.frame = headerFrame.rectangleWithWidth(nameAreaWidth)
        this.nameLabel.frame = headerFrame
            .rectangleByAddingX(iconColumnWidth)
            .rectangleWithWidth(Math.max(0, nameAreaWidth - iconColumnWidth))

        if (hasChevron) {
            this.chevronButton.frame = headerFrame
                .rectangleWithWidth(chevronWidth)
                .rectangleByAddingX(nameAreaWidth + padding * 0.25)
        }

        if (actionViews.length > 0) {
            headerFrame
                .rectangleWithWidth(actionsWidth, 1)
                .distributeViewsAlongWidth(
                    actionViews,
                    actionViews.map(() => 0),
                    actionGap,
                    actionWidths
                )
        }

        this._metadataHeight = this.metadataLabel.intrinsicContentHeight(bounds.width) + padding * 0.5

        this.metadataLabel.frame = this.contentContainer.contentBoundsWithInsets(
            0, 0, padding * 0.5, 0
        ).rectangleWithHeight(this._metadataHeight - padding * 0.5)

        this.metadataLabel.style.transform = this._expanded ? "translateY(0%)" : "translateY(-100%)"
        this.contentContainer.style.height = this._expanded ? `${this._metadataHeight}px` : "0px"

        this.contentContainer.frame = headerFrame
            .rectangleForNextRow(0, this._expanded ? this._metadataHeight : 0)
    }

    override intrinsicContentHeight(constrainingWidth = 0) {
        if (this._isCompactDownload() && !this._isUploading) {
            return RHForms.fileItemCompactMinRowHeight
        }

        if (!this._expanded || !this._hasMetadata()) {
            return RHForms.fileItemMinRowHeight
        }

        const padding = this.core.paddingLength
        const metadataWidth = Math.max(0, constrainingWidth - padding)
        const metadataHeight = this.metadataLabel.intrinsicContentHeight(metadataWidth) + padding * 0.5

        return RHForms.fileItemMinRowHeight + metadataHeight
    }

}
